import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function FishHoekPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'Fish Hoek Painters Cape Town | Family-Friendly Painting Services | False Bay Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services in Fish Hoek, Cape Town. Specialist residential and commercial painting for False Bay properties. Expert coastal protection and family-friendly services.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Fish Hoek painters, False Bay painting Cape Town, coastal property painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/fish-hoek`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Fish Hoek Painters Cape Town',
        description: 'Professional painting services in Fish Hoek, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/fish-hoek`,
        areaServed: {
          '@type': 'Place',
          name: 'Fish Hoek, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -34.1333,
            longitude: 18.4333,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('Fish Hoek Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Family Home Painting',
      description:
        'Comprehensive painting services for Fish Hoek\'s family homes using child-safe, low-VOC materials. Our family-friendly approach ensures minimal disruption to daily routines while delivering exceptional results. We understand the unique needs of busy families and offer flexible scheduling, including weekend and after-hours services to accommodate your lifestyle.',
      features: [
        'Child-safe materials',
        'Low-VOC paints',
        'Flexible scheduling',
        'Weekend services',
        'Family coordination',
        'Quick turnaround'
      ],
    },
    {
      icon: 'ri-building-2-line',
      title: 'Beachfront Property Painting',
      description:
        'Specialized services for properties near Fish Hoek Beach with marine-grade coatings designed to withstand False Bay\'s coastal conditions. Our protective systems defend against salt air, UV exposure, and moisture while maintaining beautiful aesthetics that complement the beach lifestyle and enhance property value.',
      features: [
        'Marine-grade coatings',
        'Salt air protection',
        'UV resistance',
        'Moisture barriers',
        'Beach property expertise',
        'Long-term durability'
      ],
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial & Retail Painting',
      description:
        'Professional painting for Fish Hoek\'s shops, restaurants, and businesses along Main Road and the beachfront. We understand the importance of maintaining business operations and offer after-hours services to minimize disruption. Our efficient processes ensure quick turnaround while delivering professional results that enhance your brand image.',
      features: [
        'After-hours service',
        'Minimal disruption',
        'Brand color matching',
        'Quick completion',
        'Retail expertise',
        'Professional finishes'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Coastal Painting',
      description:
        'Comprehensive exterior painting services specifically designed for Fish Hoek\'s False Bay coastal environment. Our weather-resistant systems protect against salt spray, wind, and intense sun exposure while maintaining stunning aesthetics. We use premium products formulated for coastal conditions to ensure long-lasting beauty and protection.',
      features: [
        'Weather-resistant systems',
        'Coastal protection',
        'Wind resistance',
        'Sun protection',
        'Color retention',
        'Extended warranty'
      ],
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Interior Residential Painting',
      description:
        'Expert interior painting services for Fish Hoek homes featuring premium finishes, custom color consultation, and meticulous attention to detail. We work with eco-friendly, low-odor materials perfect for family homes, ensuring a safe and comfortable environment throughout the painting process and beyond.',
      features: [
        'Premium finishes',
        'Color consultation',
        'Low-odor materials',
        'Furniture protection',
        'Detailed preparation',
        'Clean workmanship'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Maintenance & Restoration',
      description:
        'Comprehensive maintenance and restoration services for Fish Hoek properties experiencing weather damage or aging. Our expert approach addresses both aesthetic and structural concerns, including crack repair, waterproofing, and protective coatings to ensure your property remains beautiful and protected against coastal elements.',
      features: [
        'Weather damage repair',
        'Crack restoration',
        'Waterproofing',
        'Preventive maintenance',
        'Structural protection',
        'Long-term solutions'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Property Assessment',
      description:
        'Comprehensive evaluation of your Fish Hoek property, including inspection of coastal exposure, existing paint condition, and specific environmental challenges. We assess salt air impact, UV damage, and moisture issues to develop a customized protection strategy tailored to False Bay coastal conditions.',
    },
    {
      step: 2,
      title: 'Material Selection',
      description:
        'Expert consultation on the best paints and coatings for your property, considering coastal conditions and family needs. We recommend eco-friendly, low-VOC products that are safe for families while providing superior protection against Fish Hoek\'s coastal environment and delivering beautiful, long-lasting results.',
    },
    {
      step: 3,
      title: 'Surface Preparation',
      description:
        'Thorough preparation including cleaning, repairs, and primer application. Our detailed prep work ensures optimal adhesion and longevity of the paint system, addressing any underlying issues before painting begins. We take extra care to protect your property and belongings throughout the process.',
    },
    {
      step: 4,
      title: 'Professional Application',
      description:
        'Expert application by experienced painters using quality techniques and equipment. We employ proper coating systems with adequate drying times between coats to ensure maximum durability and a flawless finish. Our team works efficiently while maintaining the highest quality standards.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Thorough quality control inspection of all painted surfaces, checking for uniform coverage, proper adhesion, and excellent finish. We ensure every detail meets our high standards and your expectations before project completion, with a comprehensive walkthrough to ensure your complete satisfaction.',
    },
    {
      step: 6,
      title: 'Maintenance Guidance',
      description:
        'Detailed maintenance recommendations and care instructions to maximize the lifespan of your new paint system. We provide guidance on cleaning, inspection schedules, and preventive maintenance specific to Fish Hoek\'s coastal environment, helping you protect your investment for years to come.',
    },
  ];

  const recentProjects = [
    {
      title: 'Beachfront Family Home Renovation',
      location: 'Fish Hoek Beach Road',
      description:
        'Complete exterior and interior painting of a 4-bedroom family home with ocean views. Project included marine-grade exterior coatings, eco-friendly interior paints, and comprehensive waterproofing. Completed with minimal disruption to the family\'s daily routine using flexible scheduling.',
      image:
        'https://public.readdy.ai/ai/img_res/12a7f862b4e5ee3bde5ff5874e958557.jpg',
    },
    {
      title: 'Main Road Commercial Complex',
      location: 'Fish Hoek Main Road',
      description:
        'Comprehensive painting of a 6-unit retail complex including shops and offices. Project completed using after-hours scheduling to minimize business disruption. Included exterior facade refresh, interior office painting, and specialized coatings for high-traffic areas.',
      image:
        'https://readdy.ai/api/search-image?query=Commercial%20retail%20complex%20painting%20in%20Fish%20Hoek%2C%20Main%20Road%20shops%20and%20offices%2C%20professional%20commercial%20painting%20Cape%20Town%2C%20modern%20retail%20property%20with%20coastal%20protection&width=400&height=300&seq=fish-hoek-project-2&orientation=landscape',
    },
    {
      title: 'Coastal Cottage Restoration',
      location: 'Fish Hoek Valley',
      description:
        'Restoration of a charming 1950s cottage including exterior weatherboard painting, interior period-appropriate finishes, and modern coastal protection systems. Project preserved the cottage\'s character while providing contemporary weather resistance and energy efficiency.',
      image:
        'https://readdy.ai/api/search-image?query=Coastal%20cottage%20restoration%20painting%20in%20Fish%20Hoek%2C%20vintage%201950s%20home%20with%20period%20features%2C%20professional%20restoration%20painting%2C%20charming%20residential%20property%20Cape%20Town&width=400&height=300&seq=fish-hoek-project-3&orientation=landscape',
    },
    {
      title: 'Modern Family Home Makeover',
      location: 'Fish Hoek Heights',
      description:
        'Complete interior and exterior transformation of a contemporary family home. Included custom color scheme development, premium finishes throughout, and specialized coatings for coastal protection. Project completed during school holidays to accommodate family schedule.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20family%20home%20painting%20in%20Fish%20Hoek%2C%20contemporary%20residential%20property%20with%20mountain%20views%2C%20professional%20painting%20services%2C%20stylish%20coastal%20home%20design%20Cape%20Town&width=400&height=300&seq=fish-hoek-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Thompson',
      location: 'Fish Hoek Beachfront',
      rating: 5,
      text:
        'Excellent service for our beachfront home. The team was professional, punctual, and very considerate of our young children. The marine-grade coatings have held up beautifully for two years despite the coastal conditions. Highly recommend for family homes.',
    },
    {
      name: 'David Marais',
      location: 'Fish Hoek Main Road Business',
      rating: 5,
      text:
        'Outstanding commercial painting service. They worked after hours so we didn\'t lose any business time. The quality is exceptional and our customers have commented on how fresh and professional everything looks. Great value and service.',
    },
    {
      name: 'Jennifer van der Walt',
      location: 'Fish Hoek Valley Home',
      rating: 5,
      text:
        'The restoration of our cottage was handled with care and expertise. They understood the importance of preserving its character while providing modern protection. The results exceeded our expectations and the process was smooth and stress-free.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Fish%20Hoek%20beach%20and%20residential%20properties%20Cape%20Town%20being%20painted%2C%20family-friendly%20coastal%20homes%20near%20False%20Bay%2C%20professional%20painters%20working%20on%20beachfront%20properties%2C%20charming%20seaside%20community%20with%20mountain%20backdrop&width=1920&height=600&seq=fish-hoek-hero-main&orientation=landscape"
            alt="Professional painting services in Fish Hoek, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Fish Hoek Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Family-friendly painting services for Fish Hoek&apos;s coastal properties.
              Specialized coastal protection and eco-friendly solutions for homes and businesses in this charming False Bay community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for Fish Hoek&apos;s Coastal Community
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Fish Hoek is one of Cape Town&apos;s most beloved coastal suburbs, renowned for its family-friendly atmosphere, beautiful False Bay beach, and strong community spirit. This charming seaside neighborhood demands painting services that understand both the unique challenges of coastal living and the needs of busy families who call this area home.
              </p>
              <p>
                Our specialized painting services for Fish Hoek properties combine quality materials, expert craftsmanship, and deep understanding of False Bay coastal environmental factors. The area&apos;s exposure to salt-laden air from False Bay, intense UV radiation, and moisture-rich conditions requires proper coating systems and careful application techniques that protect your investment while maintaining the aesthetic appeal that makes Fish Hoek special.
              </p>
              <p>
                Whether you own a beachfront family home with ocean views, manage a business along Main Road, or have a property in the valley, our team delivers painting solutions that protect your investment while enhancing its appearance. We understand that Fish Hoek properties represent important family investments, and our services are designed to preserve and enhance their value through superior protection and beautiful results.
              </p>
              <p>
                The unique character of Fish Hoek, with its mix of beachfront properties, valley homes, and commercial establishments, creates diverse painting requirements. From protecting beachfront homes against salt spray to maintaining the appearance of Main Road businesses, our team has the expertise and experience to handle any project with professionalism and attention to detail.
              </p>
              <p>
                Fish Hoek&apos;s strong sense of community and family-oriented lifestyle means we take extra care to minimize disruption and work around family schedules. We offer flexible timing, use eco-friendly low-VOC materials safe for children and pets, and maintain clean, organized work sites. Our goal is to enhance your property while respecting your lifestyle and the community values that make Fish Hoek such a special place to live.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Painting Services in Fish Hoek
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Fish Hoek&apos;s family homes, beachfront properties, and local businesses. Our services are tailored to meet the needs of this family-friendly coastal community.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding Fish Hoek&apos;s False Bay Coastal Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Fish Hoek&apos;s location on False Bay presents specific environmental challenges that require appropriate painting approaches. The neighborhood&apos;s proximity to the beach means properties are exposed to salt-laden air, which can cause premature paint failure and surface degradation if not properly addressed with coastal-appropriate protection systems.
              </p>
              <p>
                The prevailing south-easterly winds during summer months carry salt spray and moisture from False Bay, affecting properties throughout the area. This salt accumulation on painted surfaces accelerates deterioration and can lead to blistering, peeling, and discoloration if standard painting products are used. Our coastal protection systems are designed to resist these conditions effectively.
              </p>
              <p>
                UV radiation intensity in Fish Hoek is high due to the reflective properties of False Bay and the area&apos;s sunny climate. This intense sun exposure causes fading and chalking of inferior paint products, making the selection of UV-resistant coatings important for maintaining long-term aesthetic appeal and protection in this coastal environment.
              </p>
              <p>
                Moisture management is important for Fish Hoek properties due to the combination of ocean humidity, morning fog, and occasional wind-driven rain. This moisture can affect building surfaces if proper coating systems are not employed. Our approach addresses these moisture challenges through proper preparation techniques and appropriate protection systems designed for coastal conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Professional Painting Process for Fish Hoek Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring quality results and lasting protection for your coastal property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Fish Hoek Properties Choose Our Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Coastal Expertise',
                    description:
                      'Specialized knowledge of False Bay coastal painting challenges and appropriate solutions for Fish Hoek properties. Our team understands the specific requirements for lasting protection in this coastal environment.',
                  },
                  {
                    title: 'Family-Friendly Approach',
                    description:
                      'Eco-friendly materials and flexible scheduling designed to accommodate busy family lifestyles. We use low-VOC, child-safe products and work around your schedule to minimize disruption to daily routines.',
                  },
                  {
                    title: 'Quality Materials',
                    description:
                      'Premium paints and coatings providing excellent protection against coastal conditions. We use quality products that deliver beautiful, long-lasting results while being safe for families and the environment.',
                  },
                  {
                    title: 'Local Knowledge',
                    description:
                      'Deep understanding of Fish Hoek\'s community, architectural styles, and local requirements. We navigate the unique aspects of this neighborhood with ease and respect for the community values.',
                  },
                  {
                    title: 'Professional Service',
                    description:
                      'Experienced team committed to delivering quality workmanship and excellent customer service. We take pride in our work and ensure every project meets high standards of quality and professionalism.',
                  },
                  {
                    title: 'Comprehensive Warranty',
                    description:
                      'Solid warranty coverage on both materials and workmanship, providing peace of mind for your investment. Our guarantees reflect our confidence in the quality and durability of our work.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Fish%20Hoek%20coastal%20property%2C%20family%20home%20painting%20near%20False%20Bay%2C%20quality%20painting%20equipment%20and%20materials%2C%20residential%20property%20maintenance%20Cape%20Town&width=600&height=700&seq=fish-hoek-team-work&orientation=portrait"
                alt="Professional painting services in Fish Hoek"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Fish Hoek Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our painting projects in Fish Hoek, demonstrating our commitment to
              quality and service in this family-friendly coastal community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Fish Hoek Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied homeowners and businesses in Fish Hoek
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Enhance Your Fish Hoek Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a professional consultation and detailed quote. Experience quality
            painting services designed specifically for Fish Hoek&apos;s coastal community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}